import React from 'react';
import NextLink from 'next/link';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import ReactGA from 'react-ga4';
import { Box, Container, Typography, Link } from '@mui/material';
import { useStyles } from './styles';
import { blackNovemberPromotionData, isUserEligibleForBlackNovember } from 'utils';
import CustomButton from 'components/CustomButton';
import BaseFaq from 'components/Faq';
import useIsMobile from 'hooks/useIsMobile';
import LazyLoad from 'react-lazyload';
import { useAuthUser } from 'features/auth/AuthUserContext';

const DynamicTestimonials = dynamic(() => import('components/Testimonials'));

export default function MainContent() {
  const classes = useStyles();
  const { user, isAuthenticated, profile } = useAuthUser();
  const promotionData = blackNovemberPromotionData();
  // const checkoutUrl = '/my-account/billing/checkout?promotion=black_november';
  const checkoutUrl = '/pricing';
  const isUserEligible = isAuthenticated ? isUserEligibleForBlackNovember(user, profile) : true;
  const isMobile = useIsMobile();

  function sendButtonClickTrackingEvents() {
    ReactGA.event({
      category: 'landing_page',
      action: 'black_november_button_click',
      label:
        'click on the "Subscribe Now" or "Subscribe for 1 dollar" button on the black november page',
      value: user.id,
    });
  }

  return (
    <>
      <Box bgcolor="grey.200">
        <Container className={classes.headerContainer}>
          <div className={classes.header}>
            <div className={classes.headerTextBox}>
              <Image
                src="/images/black-november-2023.svg"
                width={276}
                height={112}
                alt="Mind the Graph Black November"
                priority
              />

              <Box mt={2}>
                <Typography variant="h6">
                  A whole month enjoying all the <br /> benefits of a Researcher <br /> subscription
                  for just 1 USD*.
                </Typography>
              </Box>
            </div>

            {promotionData.isActive ? (
              <div className={classes.headerPlanBox}>
                <Typography variant="h4">
                  <b>Now or never!</b>
                </Typography>

                <Box mt={2}>
                  <Typography variant="h6">RESEARCHER PLAN</Typography>
                  <Typography variant="h6">
                    <del>19 USD</del> / 1 USD*
                  </Typography>
                </Box>

                <Box mt={4}>
                  <NextLink href={checkoutUrl} passHref={true}>
                    <CustomButton
                      disabled={!isUserEligible}
                      onClick={sendButtonClickTrackingEvents}
                    >
                      Subscribe Now
                    </CustomButton>
                  </NextLink>
                </Box>

                <Box mt={8} textAlign="left">
                  <Typography variant="caption" color="textSecondary">
                    * Exclusive condition for new users valid only for the first month. <br />
                    Upgrade available for Starter and Student subscribers.
                  </Typography>
                </Box>
              </div>
            ) : (
              <div className={classes.headerPlanBox}>
                <Typography className={classes.primaryColor} component="p" variant="h6">
                  <b>The Black November has expired</b>
                </Typography>

                <Box mt={2}>
                  <Typography>
                    But don't forget to check our <br /> plans and benefits. Click the button <br />{' '}
                    below to get started now.
                  </Typography>
                </Box>

                <Box mt={4}>
                  <NextLink href="/pricing" passHref={true}>
                    <CustomButton>Learn More</CustomButton>
                  </NextLink>
                </Box>
              </div>
            )}
          </div>
        </Container>
      </Box>

      <Container>
        <div className={classes.featuresBox}>
          <Typography className={classes.featuresBoxTitle} variant="h4">
            <b>
              The best infographic maker for becoming <br /> a successful author
            </b>
          </Typography>

          <Typography className={classes.featuresBoxSubtitle} variant="h5">
            Researcher Subscription Benefits:
          </Typography>

          <div className={classes.featureGrid}>
            <div className={classes.featureItem}>
              <div>
                <Image
                  src="/images/unlimited-scientific-illustrations-icon.svg"
                  width={50}
                  height={60}
                  alt="Unlimited scientific illustrations icon"
                  loading="lazy"
                />
              </div>

              <Typography variant="h6" component="p">
                Unlimited <br /> Scientific <br /> Illustrations
              </Typography>
            </div>

            <div className={classes.featureItem}>
              <div>
                <Image
                  src="/images/watermark-free-icon.svg"
                  width={50}
                  height={60}
                  alt="Watermark free icon"
                  loading="lazy"
                />
              </div>

              <Typography variant="h6" component="p">
                Watermark-free <br /> illustrations
              </Typography>
            </div>

            <div className={classes.featureItem}>
              <div>
                <Image
                  src="/images/priority-support-icon.svg"
                  alt="Priority support icon"
                  width={50}
                  height={45}
                  loading="lazy"
                />
              </div>

              <Typography variant="h6" component="p">
                Priority <br /> customer
                <br /> support
              </Typography>
            </div>
          </div>

          <div className={classes.featureGrid}>
            <div className={classes.featureItem}>
              <div>
                <Image
                  src="/images/download-formats-icon.svg"
                  width={50}
                  height={38}
                  alt="Download formats icon"
                  loading="lazy"
                />
              </div>

              <Typography variant="h6" component="p">
                Download in <br /> print-friendly formats <br /> (PNG, PDF and TIFF)
              </Typography>
            </div>

            <div className={classes.featureItem}>
              <div>
                <Image
                  src="/images/illustration-request-icon.svg"
                  width={50}
                  height={67}
                  alt="Illustration request icon"
                  loading="lazy"
                />
              </div>

              <Typography variant="h6" component="p">
                5 custom <br /> illustrations request <br /> per month
              </Typography>
            </div>

            <div className={classes.featureItem}>
              <div>
                <Image
                  src="/images/unlimited-creations-icon.svg"
                  width={50}
                  height={49}
                  alt="Unlimited creations icon"
                  loading="lazy"
                />
              </div>

              <Typography variant="h6" component="p">
                Unlimited <br /> creations
              </Typography>
            </div>
          </div>

          <Box textAlign="center" mt={4}>
            <NextLink href={checkoutUrl} passHref={true}>
              <CustomButton
                disabled={!promotionData.isActive || !isUserEligible}
                onClick={sendButtonClickTrackingEvents}
              >
                Subscribe for 1 Dollar
              </CustomButton>
            </NextLink>
          </Box>
        </div>
      </Container>

      <Box pt={18} pb={8} mt={-10} bgcolor="grey.200">
        <Container>
          <Box mb={6} textAlign="center">
            <Typography className={classes.primaryColor} variant="h4">
              <b>Papers with infographics have</b>
            </Typography>
          </Box>

          <div className={classes.infoContent}>
            <div className={classes.infoBox}>
              <Typography variant="h3">
                <b>3x</b>
              </Typography>
              <Typography>more downloads</Typography>
            </div>

            <div className={classes.infoBox}>
              <Typography variant="h3">
                <b>15x</b>
              </Typography>
              <Typography>more citations</Typography>
            </div>

            <div className={classes.infoBox}>
              <Typography variant="h3">
                <b>8x</b>
              </Typography>
              <Typography>
                more shares on <br /> social media
              </Typography>
            </div>

            <div className={classes.infoBox}>
              <Typography variant="h3">
                <b>4.8x</b>
              </Typography>
              <Typography>more retweets</Typography>
            </div>
          </div>

          <Box textAlign="center" mt={4}>
            <Typography className={classes.primaryColor}>
              <b>Source</b>: Cactus Communications internal research
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box py={8} bgcolor="grey.200">
        <Container>
          <DynamicTestimonials
            actionButton={{
              label: 'Subscribe for 1 Dollar',
              href: checkoutUrl,
              disabled: !promotionData.isActive || !isUserEligible,
              onClick: sendButtonClickTrackingEvents,
            }}
          />
        </Container>
      </Box>

      <Box py={8}>
        <LazyLoad>
          <Container>
            <BaseFaq
              items={[
                {
                  question: 'How long does the Black November campaign last?',
                  answer: 'The black november campaign goes from November 1st until November 30th.',
                },
                {
                  question: 'What payment method can I use to subscribe?',
                  answer: 'You can subscribe using your credit card.',
                },
                {
                  question: 'How does the monthly subscription work?',
                  answer:
                    "After confirming your payment details, the special price of 1 USD will be charged to your credit card. Then you can enjoy Mind the Graph's benefits for the next 30 days. Renewal is automatic and after the first month you will be charged the regular price of 25 USD.",
                },
                {
                  question: 'How can I request an illustration?',
                  answer: (
                    <>
                      Once you sign up on Black November 2023 campaign, you become a Researcher
                      subscriber and you'll be able to request our team up to 5 custom made
                      illustrations every month. That's 60 illustrations a year made just for you by
                      a senior team of illustrators. To submit your request is very simple,{' '}
                      <Link
                        href="https://mindthegraph.com/blog/faq/how-to-request-a-custom-illustration/"
                        target="_blank"
                        underline="hover"
                      >
                        just follow these steps
                      </Link>
                      .
                    </>
                  ),
                },
                {
                  question: 'What is a monthly Researcher Subscription?',
                  answer:
                    "The monthly researcher subscription is a plan in which you can enjoy all Mind the Graph's premium benefits only for a month. The monthly subscription is paid once a month on your credit card, and after the billing, you can use the tool freely for the next 30 days.",
                },
                {
                  question: "Who can enjoy Mind the Graph's Black November offer?",
                  answer:
                    'This special offer is available for new Researcher subscribers and also for Starter and Student subscribers who wish to upgrade their experience.',
                },
                {
                  question: 'Is Mind the Graph a trustworthy company?',
                  answer: (
                    <>
                      We are humans just like you, don't hesitate to meet our values and team
                      members on a special page we made for you to get to know us better, the{' '}
                      <NextLink href="/about-us" passHref={true}>
                        <Link target="_blank" underline="hover">
                          About Us
                        </Link>
                      </NextLink>{' '}
                      page. Also, we gather all our feedbacks and nourish automatically this really
                      cool mural where you can{' '}
                      <NextLink href="/feedbacks" passHref={true}>
                        <Link target="_blank" underline="hover">
                          check what every user is talking about us
                        </Link>
                      </NextLink>
                      . At last, we are a{' '}
                      <Link href="https://cactusglobal.com/" target="_blank" underline="hover">
                        Cactus Communications (CACTUS) brand
                      </Link>
                      , a technology company accelerating scientific advancement with over 2,500
                      experts working for more than 874,000 clients in 191 countries that has 8
                      different products so far to help scientists perform at its best.
                    </>
                  ),
                },
              ]}
            />
          </Container>
        </LazyLoad>
      </Box>

      <LazyLoad>
        <Box mb={-1}>
          <Link
            href="https://researcher.life/checkout-subscribe?pack_machine_name=all-access-tools-monthly&coupon=BLACKNOV23"
            target="_blank"
            underline="hover"
          >
            {!isMobile && (
              <Image
                src="/images/black-november/researcher-life-black-november-banner.png"
                width={2880}
                height={730}
                alt="researcher life banner"
                loading="lazy"
                responsive
              />
            )}

            {isMobile && (
              <Box bgcolor="#1A1818" textAlign="center">
                <Image
                  src="/images/black-november/researcher-life-black-november-mobile.png"
                  width={360}
                  height={640}
                  alt="researcher life banner mobile"
                  loading="lazy"
                />
              </Box>
            )}
          </Link>
        </Box>
      </LazyLoad>
    </>
  );
}
